import type { RefObject } from 'preact'
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'

/**
 * A device's share of the OS input while it's `active` (up, and the game playing): back
 * (Esc, B) calls `onBack`, and a gamepad starts on the first app. Up, down and confirm
 * move DOM focus through the device (`[data-screen]`) like any menu (`useUiIntents`).
 */
export function useDeviceInput(
  root: RefObject<HTMLElement | null>,
  active: boolean,
  onBack: () => void,
): void {
  const client = useClient()
  const gamepad = useView((view) => view.input.device === 'gamepad')
  const onBackRef = useRef(onBack)
  useLayoutEffect(() => {
    onBackRef.current = onBack
  })

  useEffect(() => {
    if (!client || !active) return
    return client.onUiIntent((intent) => {
      if (intent === 'back') onBackRef.current()
    })
  }, [client, active])

  useEffect(() => {
    const element = root.current
    if (!element) return
    if (active && gamepad) {
      element.querySelector<HTMLElement>('[data-app]')?.focus({ focusVisible: true })
    } else if (!active && element.contains(document.activeElement)) {
      // Hidden buttons mustn't keep the keyboard's focus.
      ;(document.activeElement as HTMLElement).blur()
    }
  }, [root, active, gamepad])
}
